import { useState } from "react";
import {
  PixelConfigResponse,
  CreatePixelConfigRequest,
  UpdatePixelConfigRequest,
  PixelProvider,
} from "@codway-dev/shared";
import { usePixels } from "../hooks/usePixels";
import PixelForm from "./pixels/PixelForm";

const PROVIDER_LABELS: Record<PixelProvider, string> = {
  META: "Meta",
  GOOGLE: "Google",
  TIKTOK: "TikTok",
};

export default function PixelsPage() {
  const { pixels, isLoading, error, createPixel, updatePixel } = usePixels();

  // null = form closed; { } = creating; { pixel } = editing that pixel.
  const [form, setForm] = useState<null | {
    pixel: PixelConfigResponse | null;
  }>(null);

  const closeForm = () => setForm(null);

  const handleSave = async (body: CreatePixelConfigRequest) => {
    if (form?.pixel) {
      await updatePixel(form.pixel.id, body);
    } else {
      await createPixel(body);
    }
    closeForm();
  };

  const handleDelete = async (body: UpdatePixelConfigRequest) => {
    if (!form?.pixel) return;
    await updatePixel(form.pixel.id, body);
    closeForm();
  };

  return (
    <s-page heading="Píxeles">
      <s-stack direction="block" gap="large-200">
        <s-banner dismissible>
          <s-text>
            Configura píxeles de seguimiento (Meta, Google y TikTok) para medir
            la conversión de tus pedidos COD.
          </s-text>
        </s-banner>

        {error && (
          <s-banner tone="critical">
            <s-text>{error}</s-text>
          </s-banner>
        )}

        <s-stack
          direction="inline"
          alignItems="center"
          justifyContent="space-between"
        >
          <s-heading>
            <strong>Tus píxeles</strong>
          </s-heading>
          {!form && (
            <s-button
              variant="primary"
              icon="plus"
              onClick={() => setForm({ pixel: null })}
            >
              Añadir píxel
            </s-button>
          )}
        </s-stack>

        {form && (
          <PixelForm
            key={form.pixel?.id ?? "new"}
            initial={form.pixel ?? undefined}
            onSave={handleSave}
            onDelete={form.pixel ? handleDelete : undefined}
            onCancel={closeForm}
          />
        )}

        {/* The list is hidden while a form is open so its row actions can't
            remount PixelForm (via its key) and silently discard in-progress
            edits. The form is the only interactive surface meanwhile. */}
        {form ? null : isLoading ? (
          <s-box
            background="base"
            border="small subdued solid"
            borderRadius="large-100"
            padding="large-400"
          >
            <s-stack direction="block" gap="base" alignItems="center">
              <s-spinner />
              <s-text color="subdued">Cargando píxeles…</s-text>
            </s-stack>
          </s-box>
        ) : pixels.length === 0 ? (
          <s-box
            background="base"
            border="small subdued solid"
            borderRadius="large-100"
            padding="large-400"
          >
            <s-stack direction="block" gap="base" alignItems="center">
              <s-icon type="chart-line" />
              <s-heading>
                <strong>Aún no tienes píxeles</strong>
              </s-heading>
              <s-text color="subdued">
                Añade tu primer píxel para empezar a medir conversiones.
              </s-text>
            </s-stack>
          </s-box>
        ) : (
          <s-section padding="none">
            <s-table>
              <s-table-header-row>
                <s-table-header>Tipo</s-table-header>
                <s-table-header>Label</s-table-header>
                <s-table-header>ID</s-table-header>
                <s-table-header>Acciones</s-table-header>
              </s-table-header-row>
              <s-table-body>
                {pixels.map((pixel) => (
                  <s-table-row key={pixel.id}>
                    <s-table-cell>
                      {PROVIDER_LABELS[pixel.provider]}
                    </s-table-cell>
                    <s-table-cell>{pixel.label ?? "—"}</s-table-cell>
                    <s-table-cell>{pixel.pixel_id}</s-table-cell>
                    <s-table-cell>
                      <s-button
                        variant="tertiary"
                        icon="edit"
                        onClick={() => setForm({ pixel })}
                      >
                        Editar
                      </s-button>
                    </s-table-cell>
                  </s-table-row>
                ))}
              </s-table-body>
            </s-table>
          </s-section>
        )}
      </s-stack>
    </s-page>
  );
}
